import "./index.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocket } from "./hooks/useWebSocket";
import { useEmulator } from "./hooks/useEmulator";
import { EmulatorView } from "./components/EmulatorView";
import { ThoughtStream } from "./components/ThoughtStream";
import { GameStatePanel } from "./components/GameStatePanel";
import { ActionLog } from "./components/ActionLog";
import { ControlPanel } from "./components/ControlPanel";
import { GamePromptEditor } from "./components/GamePromptEditor";
import { ApiKeyPanel } from "./components/ApiKeyPanel";
import { HeroExplainer } from "./components/HeroExplainer";
import type { AgentStatus, ActionRecord, PlaybackSpeed, ReplayAction, CostData } from "./server/types";

export function App() {
  const ws = useWebSocket();
  const emulator = useEmulator(ws);

  const [thoughts, setThoughts] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [actions, setActions] = useState<ActionRecord[]>([]);
  const [status, setStatus] = useState<AgentStatus>("idle");
  const [isRunning, setIsRunning] = useState(false);
  const [deaths, setDeaths] = useState(0);
  const [rewinds, setRewinds] = useState(0);
  const [attempt, setAttempt] = useState(1);
  const [bestFrames, setBestFrames] = useState<number | null>(null);
  const [speed, setSpeed] = useState<PlaybackSpeed>("1x");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [currentToolCall, setCurrentToolCall] = useState<string | null>(null);
  const [gamePrompt, setGamePrompt] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [hasRunCompleted, setHasRunCompleted] = useState(false);
  const [pendingReplayActions, setPendingReplayActions] = useState<ReplayAction[] | null>(null);
  const [cost, setCost] = useState<CostData | null>(null);
  const [publicMode, setPublicMode] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [activeRuns, setActiveRuns] = useState(0);
  const [maxRuns, setMaxRuns] = useState(3);
  const [hasDemoReplay, setHasDemoReplay] = useState(false);
  const [agentError, setAgentError] = useState<string | null>(null);
  const [isDemoPlaying, setIsDemoPlaying] = useState(false);
  const [demoProgress, setDemoProgress] = useState("");
  const [recordTab, setRecordTab] = useState(false);
  const [replayIntent, setReplayIntent] = useState<"export" | "demo" | null>(null);

  // Tab recording refs
  const tabRecorderRef = useRef<MediaRecorder | null>(null);
  const tabStreamRef = useRef<MediaStream | null>(null);
  const tabChunksRef = useRef<Blob[]>([]);
  const tabActivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTabRecording, setIsTabRecording] = useState(false);
  const [tabVideoUrl, setTabVideoUrl] = useState<string | null>(null);

  // Wall-clock timer
  const [wallTimeStart, setWallTimeStart] = useState<number | null>(null);
  const [wallTimeElapsed, setWallTimeElapsed] = useState(0);

  // Tick wall clock every second while running
  useEffect(() => {
    if (!wallTimeStart) return;
    const interval = setInterval(() => {
      setWallTimeElapsed(Math.floor((Date.now() - wallTimeStart) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [wallTimeStart]);

  const formatWallTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Stop tab recording and produce a blob
  const stopTabRecording = useCallback(() => {
    if (tabActivityTimerRef.current) {
      clearTimeout(tabActivityTimerRef.current);
      tabActivityTimerRef.current = null;
    }
    const recorder = tabRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    const stream = tabStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    tabRecorderRef.current = null;
    tabStreamRef.current = null;
    setIsTabRecording(false);
  }, []);

  // Nudge tab recorder: resume if paused, reset debounce to pause after inactivity
  const nudgeTabRecorder = useCallback(() => {
    const recorder = tabRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;

    if (recorder.state === "paused") {
      recorder.resume();
    }

    // After 2s of no messages, pause recording to skip dead time
    if (tabActivityTimerRef.current) {
      clearTimeout(tabActivityTimerRef.current);
    }
    tabActivityTimerRef.current = setTimeout(() => {
      if (recorder.state === "recording") {
        recorder.pause();
      }
    }, 2000);
  }, []);

  // Handle server messages
  useEffect(() => {
    return ws.addHandler((msg) => {
      // Keep tab recorder alive during visible activity
      if (msg.type === "claude_thought" || msg.type === "tool_call" || msg.type === "agent_status") {
        nudgeTabRecorder();
      }

      switch (msg.type) {
        case "claude_thought":
          if (msg.isComplete) {
            setIsThinking(false);
            setThoughts((prev) => prev + "\n\n---\n\n");
          } else {
            setIsThinking(true);
            setThoughts((prev) => prev + msg.text);
          }
          break;

        case "agent_status":
          setStatus(msg.status);
          if (msg.status === "thinking") {
            setCurrentToolCall(null);
          }
          if (msg.status === "idle") {
            setIsRunning(false);
            setIsThinking(false);
            setCurrentToolCall(null);
            setHasRunCompleted(true);
            stopTabRecording();
          }
          break;

        case "game_prompt":
          setGamePrompt(msg.prompt);
          break;

        case "replay_data":
          setPendingReplayActions(msg.actions);
          break;

        case "cost_update":
          setCost(msg.cost);
          break;

        case "session_info":
          setPublicMode(msg.publicMode);
          setHasApiKey(msg.hasApiKey);
          setActiveRuns(msg.activeRuns);
          setMaxRuns(msg.maxRuns);
          setHasDemoReplay(msg.hasDemoReplay);
          break;

        case "agent_error":
          setAgentError(msg.message);
          setIsRunning(false);
          break;

        case "tool_call": {
          // Format a human-readable tool call description
          const seq = msg.input._seq as string | undefined;
          const seqPrefix = seq ? `[${seq}] ` : "";
          let desc = msg.name;
          if (msg.name === "advance_frames") {
            const btns = (msg.input.buttons as string[]) || [];
            desc = `${seqPrefix}advance ${msg.input.frames}f [${btns.join("+")}]`;
          } else if (msg.name === "save_state") {
            desc = `${seqPrefix}save "${msg.input.slot_name}"`;
          } else if (msg.name === "load_state") {
            desc = `${seqPrefix}load "${msg.input.slot_name}"`;
          } else if (msg.name === "get_screenshot") {
            desc = "screenshot";
          }
          setCurrentToolCall(desc);

          setActions((prev) => [
            ...prev,
            {
              id: prev.length,
              toolName: msg.name,
              input: msg.input,
              timestamp: Date.now(),
              frameAtAction: emulator.totalFrames,
              rewound: false,
            },
          ]);
          // Track rewinds
          if (msg.name === "load_state") {
            setRewinds((r) => r + 1);
          }
          break;
        }
      }
    });
  }, [ws, emulator.totalFrames, nudgeTabRecorder, stopTabRecording]);

  // Request game prompt when connected. Deliberately keyed on the connected flag
  // only — the ws object is a fresh literal every render, and depending on it
  // re-fires this on every re-render (a message flood during thought streaming).
  useEffect(() => {
    if (ws.connected) {
      ws.sendUserAction("get_game_prompt");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.connected]);

  const handleGamePromptSave = useCallback(
    (prompt: string) => {
      setGamePrompt(prompt);
      ws.sendUserAction("set_game_prompt", { gamePrompt: prompt });
    },
    [ws]
  );

  const handleStart = useCallback(async () => {
    setAgentError(null);
    if (publicMode && !hasApiKey) {
      setAgentError("Paste your Anthropic API key first.");
      return;
    }
    setThoughts("");
    setActions([]);
    setDeaths(0);
    setRewinds(0);
    setIsRunning(true);
    setStatus("thinking");
    setCurrentToolCall(null);
    setHasRunCompleted(false);
    setVideoUrl(null);
    setTabVideoUrl(null);
    setCost(null);
    setWallTimeStart(Date.now());
    setWallTimeElapsed(0);

    // Request tab capture for full-page recording (opt-in — asking for screen
    // share unprompted is a hostile first impression)
    if (recordTab) {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: "browser" } as any,
          audio: false,
          preferCurrentTab: true,
        } as any);
        tabStreamRef.current = stream;
        tabChunksRef.current = [];

        const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
          ? "video/webm;codecs=vp9"
          : "video/webm";
        const recorder = new MediaRecorder(stream, { mimeType });
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) tabChunksRef.current.push(e.data);
        };
        recorder.onstop = () => {
          const blob = new Blob(tabChunksRef.current, { type: "video/webm" });
          const url = URL.createObjectURL(blob);
          setTabVideoUrl(url);
          tabChunksRef.current = [];
        };
        // If user stops sharing via browser UI, clean up
        stream.getVideoTracks()[0]?.addEventListener("ended", () => {
          stopTabRecording();
        });
        recorder.start(1000); // collect in 1s chunks
        tabRecorderRef.current = recorder;
        setIsTabRecording(true);
      } catch {
        // User declined screen share — continue without recording
        console.warn("[TabRecord] User declined screen capture");
      }
    }

    // Pause emulator and save starting state for replay
    emulator.pause();
    await emulator.saveReplayStart();
    ws.sendUserAction("start");
  }, [ws, emulator, stopTabRecording, publicMode, hasApiKey, recordTab]);

  const handleExportVideo = useCallback(async () => {
    setIsExporting(true);
    setExportProgress("Preparing...");
    setVideoUrl(null);
    // Save current state as replay start if not already saved
    await emulator.saveReplayStart();
    setExportProgress("Requesting replay data...");
    setPendingReplayActions(null);
    setReplayIntent("export");
    ws.sendUserAction("request_replay");
  }, [ws, emulator]);

  const handleWatchDemo = useCallback(async () => {
    setAgentError(null);
    setIsDemoPlaying(true);
    setReplayIntent("demo");
    setPendingReplayActions(null);
    ws.sendUserAction("request_replay");
  }, [ws]);

  // When replay data arrives, either record it or just play it back
  useEffect(() => {
    if (!pendingReplayActions || !replayIntent) return;
    const actions = pendingReplayActions;
    const intent = replayIntent;
    setPendingReplayActions(null);

    if (intent === "demo") {
      if (actions.length === 0) {
        setAgentError("No demo replay available.");
        setIsDemoPlaying(false);
        setDemoProgress("");
        setReplayIntent(null);
        return;
      }

      emulator
        .replay(actions, (step, total) => {
          setDemoProgress(`DEMO: ${step}/${total}`);
        })
        .catch((e) => {
          console.error("[Demo] Failed:", e);
          setAgentError("Demo playback failed.");
        })
        .finally(() => {
          setIsDemoPlaying(false);
          setDemoProgress("");
          setReplayIntent(null);
        });
      return;
    }

    if (actions.length === 0) {
      setExportProgress("No actions to replay");
      setIsExporting(false);
      setReplayIntent(null);
      return;
    }

    setExportProgress(`Replaying ${actions.length} actions...`);

    emulator
      .replayAndRecord(actions, (step, total) => {
        setExportProgress(`Replaying: ${step}/${total} actions`);
      })
      .then((blob) => {
        if (blob) {
          // Revoke old URL if any
          if (videoUrl) URL.revokeObjectURL(videoUrl);
          const url = URL.createObjectURL(blob);
          setVideoUrl(url);
          setExportProgress("Video ready!");
        } else {
          setExportProgress("Recording failed");
        }
        setIsExporting(false);
        setReplayIntent(null);
      })
      .catch((e) => {
        console.error("[Export] Failed:", e);
        setExportProgress("Export failed");
        setIsExporting(false);
        setReplayIntent(null);
      });
  }, [pendingReplayActions, replayIntent, emulator]);

  const handleSetApiKey = useCallback(
    (key: string) => {
      ws.sendUserAction("set_api_key", { apiKey: key });
    },
    [ws]
  );

  const handleClearApiKey = useCallback(() => {
    ws.sendUserAction("clear_api_key");
  }, [ws]);

  const handleStop = useCallback(() => {
    ws.sendUserAction("stop");
    setIsRunning(false);
    setStatus("idle");
    setCurrentToolCall(null);
    stopTabRecording();
  }, [ws, stopTabRecording]);

  const handleSpeedChange = useCallback(
    (newSpeed: PlaybackSpeed) => {
      setSpeed(newSpeed);
      emulator.setSpeed(newSpeed);
      ws.sendUserAction("change_speed", { speed: newSpeed });
    },
    [ws, emulator]
  );

  const handleModelChange = useCallback(
    (newModel: string) => {
      setModel(newModel);
      ws.sendUserAction("change_model", { model: newModel });
    },
    [ws]
  );

  // The demo replays from power-on, so it only makes sense on a fresh emulator
  const demoAvailable = hasDemoReplay && emulator.totalFrames < 60;

  return (
    <div className="min-h-screen bg-black text-nes-white">
      {/* Header */}
      <header className="border-b-2 border-nes-border px-4 py-3">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <h1 className="font-pixel text-lg">
            <span className="text-nes-gold">★</span>{" "}
            <span className="text-nes-red">NESTAS</span>{" "}
            <span className="text-nes-gold">★</span>
          </h1>
          <div className="flex items-center gap-4">
            {/* Wall clock */}
            {(isRunning || wallTimeElapsed > 0) && (
              <span className="font-pixel text-xs text-nes-white">
                {formatWallTime(wallTimeElapsed)}
              </span>
            )}
            {/* Cost */}
            {cost && (
              <span className="font-pixel text-xs text-nes-green" title={`Input: ${cost.inputTokens.toLocaleString()} | Output: ${cost.outputTokens.toLocaleString()} | Cache Read: ${cost.cacheReadTokens.toLocaleString()} | Cache Write: ${cost.cacheWriteTokens.toLocaleString()}`}>
                ${cost.totalCostUSD.toFixed(4)}
                <span className="text-nes-gray ml-1">
                  ({((cost.inputTokens + cost.outputTokens + cost.cacheReadTokens + cost.cacheWriteTokens) / 1000).toFixed(1)}k tok)
                </span>
              </span>
            )}
            {/* REC indicator */}
            {isTabRecording && (
              <span className="font-pixel text-xs text-nes-red animate-pulse">
                ● REC
              </span>
            )}
            <span className="font-pixel text-xs text-nes-gray">
              CLAUDE AI × SUPER MARIO BROS
            </span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-[1400px] mx-auto p-4 flex flex-col gap-4">
        <HeroExplainer />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
          {/* Left column: Emulator + Stats */}
          <div className="flex flex-col gap-4">
            <EmulatorView
              onCanvasReady={emulator.initEmulator}
              ready={emulator.ready}
            />
            <div className="grid grid-cols-2 gap-4">
              <GameStatePanel
                totalFrames={emulator.totalFrames}
                deaths={deaths}
                rewinds={rewinds}
                attempt={attempt}
                bestFrames={bestFrames}
                saveStates={emulator.saveStates}
              />
              <ControlPanel
                isRunning={isRunning}
                status={status}
                speed={speed}
                model={model}
                currentToolCall={currentToolCall}
                onStart={handleStart}
                onStop={handleStop}
                onSpeedChange={handleSpeedChange}
                onModelChange={handleModelChange}
                connected={ws.connected}
                emulatorReady={emulator.ready}
                hasRunCompleted={hasRunCompleted}
                isExporting={isExporting}
                exportProgress={exportProgress}
                videoUrl={videoUrl}
                onExportVideo={handleExportVideo}
                tabVideoUrl={tabVideoUrl}
                publicMode={publicMode}
                hasApiKey={hasApiKey}
                seatsFull={activeRuns >= maxRuns && !isRunning}
                agentError={agentError}
                demoAvailable={demoAvailable}
                isDemoPlaying={isDemoPlaying}
                demoProgress={demoProgress}
                onWatchDemo={handleWatchDemo}
                recordTab={recordTab}
                onRecordTabChange={setRecordTab}
              />
            </div>
          </div>

          {/* Right column: Thoughts + Actions */}
          <div className="flex flex-col gap-4">
            {publicMode && (
              <ApiKeyPanel
                hasApiKey={hasApiKey}
                isRunning={isRunning}
                onSetKey={handleSetApiKey}
                onClearKey={handleClearApiKey}
              />
            )}
            <ThoughtStream
              thoughts={thoughts}
              isThinking={isThinking}
              status={status}
              isRunning={isRunning}
            />
            <ActionLog actions={actions} />
            <GamePromptEditor
              gamePrompt={gamePrompt}
              isRunning={isRunning}
              onSave={handleGamePromptSave}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
